// @givmo/org-console — the ONE post-auth redirect-target sanitizer, for every console door.
//
// ══ WHY IT LIVES HERE ══════════════════════════════════════════════════════════════════════════
//
// Every sign-in surface in this estate carries the same `redirect` search param, and every one of
// them has to answer the same question before it navigates: is this value a path in MY app, or a
// destination somebody else chose? OM-12 adds a per-module door to that set, and a third hand-rolled
// answer to one security question is one too many — so the strictest existing implementation is
// promoted here and the doors call it.
//
// ══ WHAT IT REFUSES, AND WHY EACH ONE ══════════════════════════════════════════════════════════
//
//   * anything that is not a string — a search param can be an array or an object
//   * anything not starting with `/` — an absolute URL carries a person off this origin with their
//     intent intact, which is the whole shape of a redirect abuse
//   * `//host` and `/\host` — browsers read BOTH as protocol-relative, so they are absolute URLs
//     wearing a path's clothes, and a bare `startsWith('/')` waves them through
//   * an ASCII control character anywhere (U+0000–U+001F, U+007F; Org Machinery OM-38) — a URL
//     parser removes every tab, line feed and carriage return wherever it stands, so `/<tab>/host`
//     passes the check above and still reads as `//host`. No control character is part of a
//     console path, so the whole range is refused rather than the three a parser strips today.
//
// ⚠ A ROUTE VALIDATOR IS NOT A SUBSTITUTE. TanStack keeps UNKNOWN search params, so a
// `validateSearch` that drops the key does NOT remove it from the object handed to `beforeLoad` or
// `useSearch` — every consumer must put the value through this, not lean on the validator alone.
//
// ⚠ NOR IS A ROUTER'S OWN NORMALIZATION. The pinned router core collapses `//` today, which is why
// the looser check has never been exploitable — but that is an implementation detail of a dependency
// this estate upgrades routinely, and a door whose safety rests on it is one upgrade from unsafe.

/**
 * A post-auth bounce target that is a path in the caller's own app, or `undefined`.
 *
 * `undefined` means the caller uses its own default destination — never the untrusted value.
 */
export function sanitizeRedirectTarget(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return undefined;
  if (hasControlCharacter(value)) return undefined;
  return value;
}

/** Whether `text` holds an ASCII control character: the C0 range or DEL. */
function hasControlCharacter(text: string): boolean {
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index);
    if (code < 0x20 || code === 0x7f) return true;
  }
  return false;
}
